/**
 * Live updates.
 *
 * One socket for the whole app, opened once and re-subscribed whenever the
 * selected symbol changes -- which is what `set_symbol` on the server side
 * exists for.
 *
 * The socket carries no state of its own: a `signal.created` push
 * invalidates the React Query cache rather than mutating a store, so a
 * pushed signal and a fetched signal can never disagree.
 */

import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import type { WsMessage } from '@/api/types'
import { useAppStore } from '@/store/useAppStore'

type Handler = (message: WsMessage) => void

const RECONNECT_BASE_MS = 1000
const RECONNECT_MAX_MS = 15000

function socketUrl(): string {
  const configured = import.meta.env.VITE_WS_URL
  if (configured) return configured
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${protocol}//${window.location.host}/ws`
}

export function useWebSocket(symbolIds: number[], onMessage?: Handler): void {
  const queryClient = useQueryClient()
  const setWsConnected = useAppStore((s) => s.setWsConnected)

  const socketRef = useRef<WebSocket | null>(null)
  const retryRef = useRef(RECONNECT_BASE_MS)
  const timerRef = useRef<number>()
  const handlerRef = useRef(onMessage)
  const symbolsRef = useRef(symbolIds)
  const subscriptionKey = symbolIds.join(',')

  handlerRef.current = onMessage
  symbolsRef.current = symbolIds

  useEffect(() => {
    let disposed = false

    const connect = () => {
      if (disposed) return
      const socket = new WebSocket(socketUrl())
      socketRef.current = socket

      socket.onopen = () => {
        // A socket that is no longer the current one must not claim the slot.
        if (socketRef.current !== socket) {
          socket.close()
          return
        }
        retryRef.current = RECONNECT_BASE_MS
        setWsConnected(true)
        if (symbolsRef.current.length > 0) {
          socket.send(JSON.stringify({ action: 'subscribe', symbol_ids: symbolsRef.current }))
        }
      }

      socket.onmessage = (event) => {
        let message: WsMessage
        try {
          message = JSON.parse(event.data)
        } catch {
          return
        }

        // Push -> cache invalidation. The component tree re-renders from the
        // same data path it uses for the initial load.
        switch (message.type) {
          case 'signal.created':
          case 'signal.updated':
            queryClient.invalidateQueries({ queryKey: ['signals'] })
            queryClient.invalidateQueries({ queryKey: ['mtf'] })
            queryClient.invalidateQueries({ queryKey: ['watchlists'] })
            break
          case 'news.created':
            queryClient.invalidateQueries({ queryKey: ['news'] })
            break
          case 'event.upcoming':
            queryClient.invalidateQueries({ queryKey: ['events'] })
            break
          default:
            break
        }
        handlerRef.current?.(message)
      }

      socket.onclose = () => {
        // Only the socket that currently owns the slot may clear it.
        //
        // Without this check a superseded socket's late `onclose` wipes the
        // reference to the socket that replaced it. The connection stays
        // open and the badge still says "realtime", but `set_symbol` is
        // never sent -- so the server has a client subscribed to nothing and
        // the chart silently never ticks. React StrictMode's double-mount
        // triggers this on every page load in development, and any real
        // reconnect triggers it in production.
        if (socketRef.current !== socket) return

        socketRef.current = null
        setWsConnected(false)
        if (disposed) return
        // Exponential backoff so a backend restart does not turn into a
        // reconnect storm.
        timerRef.current = window.setTimeout(connect, retryRef.current)
        retryRef.current = Math.min(retryRef.current * 2, RECONNECT_MAX_MS)
      }

      socket.onerror = () => socket.close()
    }

    connect()
    return () => {
      disposed = true
      window.clearTimeout(timerRef.current)
      const socket = socketRef.current
      socketRef.current = null
      socket?.close()
      setWsConnected(false)
    }
  }, [queryClient, setWsConnected])

  // Update the subscription without tearing down the connection.
  //
  // The whole watchlist is subscribed, not just the charted symbol: the
  // watchlist shows a live price per row, and polling nine symbols over REST
  // to animate a number the socket is already carrying would be silly.
  useEffect(() => {
    const socket = socketRef.current
    if (socket?.readyState === WebSocket.OPEN && subscriptionKey) {
      socket.send(JSON.stringify({ action: 'subscribe', symbol_ids: subscriptionKey.split(',').map(Number) }))
    }
  }, [subscriptionKey])
}
